import { ContactLookupResultDTO, ContactJobDetailDTO } from '@/types';
import { getJobStatusStyle } from '@/lib/jobStatusStyle';
import { X, ExternalLink } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { useCompanyTimezone } from '@/hooks/useGlobalCompany';
import { formatInstantInCompanyTimeZone } from '@/lib/dateUtils';

interface ContactPastJobsModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: ContactLookupResultDTO | null;
  onJobSelect?: (jobId: string) => void;
}

export function ContactPastJobsModal({ isOpen, onClose, contact, onJobSelect }: ContactPastJobsModalProps) {
  const layer = useModalLayer({
    enabled: isOpen && !!contact,
    onClose,
    historyState: { contactPastJobsModal: true },
  });

  if (!isOpen || !contact) return null;

  const jobs = contact.jobs || [];

  const companyTz = useCompanyTimezone();

  const formatDate = (raw?: string | null) => {
    if (!raw) return 'Date not set';
    return formatInstantInCompanyTimeZone(raw, companyTz) || 'Date not set';
  };
  const handleJobClick = (job: ContactJobDetailDTO) => {
    if (onJobSelect) {
      onJobSelect(job.job_id);
    }
  };

  return createPortal(
    <LayerDepthContext.Provider value={layer.depth}>
    <div className="fixed inset-0 flex items-center justify-center bg-black/30 p-4" style={layer.style}>
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Past jobs</p>
            <h3 className="text-lg font-semibold text-gray-900">{contact.name}</h3>
          </div>
          <button
            type="button"
            onClick={layer.onClose}
            className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
          {jobs.length === 0 ? (
            <p className="text-sm text-gray-600">No past jobs found.</p>
          ) : (
            <div className="space-y-3">
              {jobs.map((job) => (
                <button
                  key={job.job_id}
                  type="button"
                  onClick={() => handleJobClick(job)}
                  className="w-full text-left rounded-lg border border-gray-200 p-3 transition-colors hover:border-blue-300 hover:bg-blue-50 cursor-pointer group"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="space-y-1">
                      <div className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                        {job.job_type_name || 'Job'} #{job.invoice_number || ''}
                        <ExternalLink className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="text-xs text-gray-600">{job.address || 'No address provided'}</div>
                    </div>
                    <div className="text-right text-xs text-gray-600">
                      <div>{formatDate(job.job_date)}</div>
                      <div className="mt-1">
                        {job.canceled_at ? (
                          <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800">
                            Canceled
                          </span>
                        ) : (
                          <span
                            className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium"
                            style={getJobStatusStyle(job.status_color)}
                          >
                            {job.status || 'Status unknown'}
                          </span>
                        )}
                      </div>
                      <div className="text-gray-900">${job.total?.toFixed(2) ?? '0.00'}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-gray-100 px-5 py-3">
          <button
            type="button"
            onClick={layer.onClose}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
    </LayerDepthContext.Provider>,
    document.body
  );
}


